import { Component, Input, OnInit } from '@angular/core';
import { AppComponent } from 'app/app.component';
import { CommunicationService } from 'app/_services/communication.service';
import { IFilter } from 'app/_models/IFilter';
import { IHeader } from 'app/_models/IHeader';
import { FilterController } from 'app/_controllers/filterController';
import { ISettings } from 'app/_models/ISettings';
import { IconDefinition, faSquareCheck, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'advanced-filters-component',
  templateUrl: './advanced-filters.component.html',
  styleUrls: ['./advanced-filters.component.css']
})
export class AdvancedFiltersComponent implements OnInit {
  //#region Fields & Icons  
  @Input() settings: ISettings;
  @Input() filters: IFilter[] = [];
  @Input() headers: IHeader[] = [];
  @Input() filterString: string;//userstring
  @Input() nodeId: boolean;
  @Input() detailViewName: string = undefined;
  @Input() serverDateFormat: string;
  faSquareCheck: IconDefinition = faSquareCheck;
  faFloppyDisk: IconDefinition = faFloppyDisk;
  query = { condition: null, rules: [] };
  config: any = { fields: {} };
  conditions: string[] = [];
  currentLogicalOperator: string = 'and';
  currentSubLogicalOperator: string = 'and';
  i: number = 0;
  j: number = 0;
  subruleset: any[] = [];
  includesDates: boolean = false;
  //#endregion

  constructor(
    public app: AppComponent,
    private communicationService: CommunicationService,
    private filterController: FilterController
  ) { }

  ngOnInit(): void {
    this.config.fields = this.filters.reduce((acc, obj) => {
      let key = obj.caption.toLowerCase().replace(/ /g, '');
      let type;

      if (obj.dataType === "Int32") {
        type = "number";
      } else if (obj.dataType === "DateTime") {
        type = "date";
      } else if (obj.dataType === "TimeSpan") {
        type = "time";
      } if (obj.dataType === "Boolean") {
        type = "boolean";
      } else {
        type = obj.dataType.toLowerCase();
      }

      acc[key] = { name: obj.caption, type: type, filter: obj };
      return acc;
    }, {});

    this.app.showLoader();
    let conditions = this.parseConditions(this.filterString);
    this.buildRules([], conditions, false);
  }

  dateInputUpdated(selectedDate: string, rule: any): void {
    rule.value = selectedDate;
  }

  parseConditions(input: string): string[] {
    let matches = input.match(/\([^()]+\)|\[[^\[\]]+\] (NOT LIKE|LIKE|=|>|<|>=|<=) '[^']*'|\[[^\[\]]+\] (NOT LIKE|LIKE|=|>|<|>=|<=) \d+|AND|OR/g);
    return matches ? matches.map(match => match.trim()) : [];
  }

  buildRules(rules: any[], conditions: string[], subClause: boolean): void {
    if ((!subClause && this.i < conditions.length) || (subClause && this.j < conditions.length)) {
      let condition = !subClause ? conditions[this.i].trim() : conditions[this.j].trim();
      if (condition.startsWith('(') && condition.endsWith(')')) {
        if (!subClause) {
          let subconditions = this.parseConditions(condition.replace(condition.charAt(0), '').replace(condition.charAt(condition.length - 1), ''));//removes parentheses                 
          this.buildRules([], subconditions, true);
          this.i++;
        } else this.j++;

        setTimeout(() => rules.push({ condition: this.currentSubLogicalOperator, rules: this.subruleset }), 1000);
        setTimeout(() => this.buildRules(rules, conditions, subClause), 1000);
      } else {
        if (condition.toUpperCase() === 'AND' || condition.toUpperCase() === 'OR') {
          if (subClause) {
            this.currentSubLogicalOperator = condition.toLowerCase();
            this.j++;
          } else {
            this.currentLogicalOperator = condition.toLowerCase();
            this.i++;
          }
          this.buildRules(rules, conditions, subClause);
        }

        let conditionMatch = condition.match(/\[([^\]]+)]\s*(NOT LIKE|LIKE|=|>=|<=|<|>|BETWEEN)\s*(.+)/i);
        if (conditionMatch) {
          let field = conditionMatch[1].trim();
          let operator = conditionMatch[2].trim();
          let value: any = conditionMatch[3].trim();

          if ((value.startsWith("'") && value.endsWith("'")) || (value.startsWith('"') && value.endsWith('"'))) {
            value = value.slice(1, -1);
          }

          if (operator == 'NOT LIKE') {
            operator = 'LIKE';
            value = `!${value}`;
          }

          let filter = this.filters.find(f => f.caption === field);
          if (filter) {
            if (filter.dataType == 'Boolean') {
              value = value == 0 ? false : true;
              rules.push({
                field: filter.caption.toLowerCase().replace(/ /g, ''),
                operator: operator.toLowerCase(),
                value: value,
              });
            } else {
              rules.push({
                field: filter.caption.toLowerCase().replace(/ /g, ''),
                operator: operator.toLowerCase(),
                value: value.replace(')', ''),
              });
            }           
          }

          if (subClause) this.j++;
          else this.i++;
          this.buildRules(rules, conditions, subClause);
        }
      }
    } else if ((!subClause && this.i == conditions.length) || (subClause && this.j == conditions.length)) {
      if (subClause) this.subruleset = rules; 
      else {
        this.query.condition = this.currentLogicalOperator;
        this.query.rules = rules;
        this.app.hideLoader();
      }

      return;
    } else {
      if (subClause) this.j++;
      else this.i++;
      this.buildRules(rules, conditions, subClause);
    }
  }

  save(): void {
    if (!this.detailViewName) {
      localStorage.removeItem(`qx_filters_advanced_${this.nodeId}`);
      localStorage.removeItem(`qx_filters_${this.nodeId}`);
    } else {
      localStorage.removeItem(`qx_filters_advanced_${this.nodeId}_${this.detailViewName}`);
      localStorage.removeItem(`qx_filters_${this.nodeId}_${this.detailViewName}`);
    }

    if (this.query.rules.length == 0) {
      this.communicationService.closeDialog();
      this.communicationService.refreshAll();
      return;
    }

    let results = this.buildFilterString(this.query.rules, this.query.condition.toUpperCase());
    let validationResult = this.filterController.validateFilterString(results.userString, this.headers, true);
    if (validationResult.includes('ERROR')) {
      this.app.alertError(validationResult);
      return;
    }

    if (!this.detailViewName)
      localStorage.setItem(`qx_filters_advanced_${this.nodeId}`, JSON.stringify({ userString: results.userString, serverString: results.serverString }));
    else
      localStorage.setItem(`qx_filters_advanced_${this.nodeId}_${this.detailViewName}`, JSON.stringify({ userString: results.userString, serverString: results.serverString }));

    this.communicationService.closeDialog();
    this.communicationService.refreshAll();
  }

  buildFilterString(rules: any[], parentCondition: string): { userString: string, serverString: string } {
    let userString = '';
    let serverString = '';
    rules.forEach((rule, index) => {
      if (rule.rules) {
        let nestedCondition = rule.condition?.toUpperCase() || parentCondition;
        let results = this.buildFilterString(rule.rules, nestedCondition);
        userString += `(${results.userString})`;
        serverString += `(${results.serverString})`;
      } else {
        let matchingField = Object.keys(this.config.fields).find(
          key => key === rule.field || this.config.fields[key].value === rule.field
        );

        if (matchingField) {
          rule.filter = this.config.fields[matchingField].filter;
          let operator = rule.operator.toUpperCase();
          let value = isNaN(rule.value) ? `'${rule.value}'` : rule.value;

          if (rule.filter.dataType == 'DateTime') {
            value = value.replace('\'', '').replace('\'', '');
            value = rule.value;
            rule.filter.ranges.from = value;
            rule.filter.ranges.userDateFrom = value;
            rule.filter.ranges.serverDateFrom = `${value.split('/')[1]}/${value.split('/')[0]}/${value.split('/')[2]}`;
            userString += `[${rule.filter.caption}] ${operator} '${rule.filter.ranges.userDateFrom}'`;
            serverString += `${rule.filter.field} ${operator} '${rule.filter.ranges.serverDateFrom}'`;
            this.includesDates = true;
          } else if (rule.filter.dataType == 'Boolean') {
            userString += `[${rule.filter.caption}] = ${value ? 1 : 0}`;
            serverString += `${rule.filter.field} = ${value ? 1 : 0}`;
          } else if (isNaN(rule.value) && value.charAt(1) == '!' && operator == 'LIKE' && rule.filter.dataType != 'DateTime') {
            operator = 'NOT LIKE';
            value = value.replace(value.charAt(1), '');
            userString += `[${rule.filter.caption}] ${operator} ${value}`;
            serverString += `${rule.filter.field} ${operator} ${value}`;
          } else {
            userString += `[${rule.filter.caption}] ${operator} ${value}`;
            serverString += `${rule.filter.field} ${operator} ${value}`;
          }
        }
      }

      if (index < rules.length - 1) {
        let condition = rule.condition?.toUpperCase() || parentCondition;
        userString += ` ${condition} `;
        serverString += ` ${condition} `;
      }
    });

    return { userString, serverString };
  }
}
